import React, { useState } from 'react';
import { auth, db } from '../firebaseConfig'; // Import Firestore
import { createUserWithEmailAndPassword } from "firebase/auth";
import { doc, setDoc } from "firebase/firestore"; // Import Firestore methods
import { sendEmailVerification } from 'firebase/auth';
import { useNavigate, Link } from 'react-router-dom';
import { sendWelcomeEmail } from '../utils/emailService';

const SignUp = ({ setIsAuthenticated }) => {
    const [email, setEmail] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordStrength, setPasswordStrength] = useState(0);
    const [error, setError] = useState('');
    const navigate = useNavigate();

    // Password strength checker
    const checkPasswordStrength = (password) => {
        let strength = 0;
        if (password.length >= 8) strength++;
        if (password.match(/[a-z]/) && password.match(/[A-Z]/)) strength++;
        if (password.match(/\d/)) strength++;
        if (password.match(/[^a-zA-Z\d]/)) strength++;
        return strength;
    };

    const handlePasswordChange = (e) => {
        const newPassword = e.target.value;
        setPassword(newPassword);
        setPasswordStrength(checkPasswordStrength(newPassword));
    };

    const handleSignup = async (e) => {
        e.preventDefault();
        setError('');

        // Validate passwords match
        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        // Validate password strength
        if (passwordStrength < 3) {
            setError('Password is not strong enough. Please include uppercase, lowercase, numbers, and special characters.');
            return;
        }

        try {
            // Create user with email and password in Firebase Auth
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;
            
            // Create a profile document in Firestore for the user
            const userProfileRef = doc(db, 'profiles', user.uid);
            await setDoc(userProfileRef, {
                userId: user.uid,
                username: username,
                email: user.email,
                role: 'simple',
                photoURL: user.photoURL || '/logo.png',
                subscriptionStatus: {
                    type: 'free',
                    endDate: null
                },
                stats: {
                    totalDreams: 0,
                    credits: 5,  // Initial free credits
                    monthlyFreeCredits: 5
                },
                followers: [],
                following: [],
                interests: [],
                bio: '',
                location: '',
                createdAt: new Date(),
                dreamCatcherLevel: 1
            });

            // Send welcome email and verification email
            await sendWelcomeEmail(user, username);

            alert('Please verify your email before logging in.');
            setIsAuthenticated(true);
            navigate('/');
            
        } catch (error) {
            console.error("Error signing up:", error);
            setError(error.message);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-indigo-900 via-purple-900 to-black flex flex-col justify-center items-center px-4">
            <div className="flex flex-col items-center mb-8">
                <Link to="/">
                    <img 
                        src="/logo.png"
                        alt="Dreamarium Logo"
                        className="w-20 h-20 rounded-full bg-black border-1"
                    />
                </Link>
                <span className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent mt-4">
                    Dreamarium
                </span>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-xl shadow-lg p-8 w-full max-w-md">
                <h1 className="text-3xl font-bold mb-6 text-center bg-gradient-to-r from-purple-500 to-pink-500 text-transparent bg-clip-text">
                    Sign Up
                </h1>
                <form onSubmit={handleSignup} className="flex flex-col space-y-4 w-full">
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                            Username
                        </label>
                        <input
                            type="text"
                            placeholder="Choose a username"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            className="w-full px-4 py-2 bg-white/10 border border-purple-400/50 rounded-lg text-white placeholder-gray-400 focus:border-purple-400 focus:ring-2 focus:ring-purple-500/30"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                            Email
                        </label>
                        <input
                            type="email"
                            placeholder="Enter your email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full px-4 py-2 bg-white/10 border border-purple-400/50 rounded-lg text-white placeholder-gray-400 focus:border-purple-400 focus:ring-2 focus:ring-purple-500/30"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                            Password
                        </label>
                        <input
                            type="password"
                            placeholder="Create a password"
                            value={password}
                            onChange={handlePasswordChange}
                            className="w-full px-4 py-2 bg-white/10 border border-purple-400/50 rounded-lg text-white placeholder-gray-400 focus:border-purple-400 focus:ring-2 focus:ring-purple-500/30"
                            required
                        />
                        <div className="mt-1">
                            <div className="flex space-x-1">
                                {[1, 2, 3, 4].map((level) => (
                                    <div
                                        key={level}
                                        className={`h-1 flex-1 rounded ${
                                            level <= passwordStrength
                                                ? 'bg-green-500'
                                                : 'bg-gray-600'
                                        }`}
                                    />
                                ))}
                            </div>
                            <p className="text-xs text-gray-400 mt-1">
                                Password strength: {['Weak', 'Fair', 'Good', 'Strong'][passwordStrength - 1] || 'Too weak'}
                            </p>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                            Confirm Password
                        </label>
                        <input
                            type="password"
                            placeholder="Confirm your password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className="w-full px-4 py-2 bg-white/10 border border-purple-400/50 rounded-lg text-white placeholder-gray-400 focus:border-purple-400 focus:ring-2 focus:ring-purple-500/30"
                            required
                        />
                    </div>

                    {error && (
                        <p className="text-red-500 text-sm text-center">{error}</p>
                    )}

                    <button 
                        type="submit" 
                        className="w-full py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:opacity-90 transition-opacity"
                    >
                        Sign Up
                    </button>
                </form>
                <p className="text-center mt-4 text-gray-300">
                    Already have an account?{" "}
                    <Link to="/login" className="text-purple-400 hover:text-purple-300">
                        Login
                    </Link>
                </p>
            </div>
        </div>
    );
};

export default SignUp;
